import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ProductMainImgWrapper from '../components/ProductDetail/ProductMainImgWrapper';
import DetailCarousel from '../components/Carousel/DetailCarousel';
import ProductMainDescription from '../components/ProductDetail/ProductMainDescription';
import ProductSubDescription from '../components/ProductDetail/ProductSubDescription';
import ScrollFixedTopButton from '../components/Button/ScrollFixedTopButton';
import ProductVideoInputWrapper from '../components/ProductDetail/ProductVideoInputWrapper';
import useApi from '../hooks/useApi';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { layoutTopState } from '../recoil/layoutTop/layoutTopAtom';
import useThumbnailUrl from '../hooks/useThumbnailUrl';
import { loadingSpinnerState } from '../recoil/loadingSpinner/loadingSpinnerAtom';
import ERROR_CODES from '../constants/errorCodes';
import SCREEN_SIZES from '../constants/screenSizes';
import PRODUCT_TYPES from '../constants/productTypes';
import * as Sentry from '@sentry/react';

const ProductVideoDetail = () => {
  const { id } = useParams();
  const [productDetailData, setProductDetailData] = useState({});
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const setIsLoading = useSetRecoilState(loadingSpinnerState);
  const { clips, product_description, thumbnail_info } = productDetailData;
  const api = useApi();
  const layoutTop = useRecoilValue(layoutTopState);
  const [isMobileScreen, setIsMobileScreen] = useState(window.innerWidth <= SCREEN_SIZES.MOBILE_SCREEN_WIDTH);

  // https://vlending.atlassian.net/browse/MUQA-313 관련 추가
  const leftWrapperRef = useRef(null);
  const rightWrapperRef = useRef(null);

  const imageUrl = useThumbnailUrl(thumbnail_info, 800);
  const navigate = useNavigate();
  const accessToken = localStorage.getItem('access_token');

  const clearSession = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('exp');
    Sentry.setUser(null);
  };

  const fetchProductDetailData = async () => {
    setIsLoading(true);
    setIsLocalLoading(true);
    try {
      const response = await api.get(`mubeat_live/product/${id}`, {
        headers: {
          Authorization: accessToken,
        },
      });
      setProductDetailData(response.data);
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ERR_BAD_RESPONSE') {
        navigate('/error');
      }

      const errorCode = error?.response && error?.response.data && error?.response.data.code;
      if (errorCode === ERROR_CODES.notFoundItem) {
        navigate('/page-error');
      } else if (errorCode === ERROR_CODES.tokenExpired) {
        clearSession();
      } else {
        console.log(error);
      }
    } finally {
      setIsLoading(false);
      setIsLocalLoading(false);
    }
  };

  const calculateElementValue = () => {
    const detailPageWrapperPaddingBottom = document.querySelector('.detail-page-wrapper');
    const detailLayoutRightWrapperPaddingTop = document.querySelector('.detail-layout-right-wrapper');
    const header = document.querySelector('.ml-header');

    let detailPageWrapperPaddingBottomValue = null;
    let detailLayoutRightWrapperPaddingTopValue = null;
    let headerOffsetHeightValue = null;

    if (detailPageWrapperPaddingBottom) {
      const paddingBottom = window.getComputedStyle(detailPageWrapperPaddingBottom).paddingBottom;
      detailPageWrapperPaddingBottomValue = parseInt(paddingBottom, 10);
    }

    if (detailLayoutRightWrapperPaddingTop) {
      const paddingTop = window.getComputedStyle(detailLayoutRightWrapperPaddingTop).paddingTop;
      detailLayoutRightWrapperPaddingTopValue = parseInt(paddingTop, 10);
    }

    if (header) {
      headerOffsetHeightValue = header.offsetHeight;
    }

    const result =
      detailPageWrapperPaddingBottomValue + (detailLayoutRightWrapperPaddingTopValue - headerOffsetHeightValue);

    return result;
  };

  useEffect(() => {
    fetchProductDetailData();
    calculateElementValue();
  }, [id, accessToken]);

  useEffect(() => {
    const handleScroll = () => {
      // 모바일 미적용
      if (!isMobileScreen && leftWrapperRef.current && rightWrapperRef.current) {
        const leftBottom = leftWrapperRef.current.getBoundingClientRect().bottom;
        const windowBottom = window.innerHeight;

        if (leftBottom + calculateElementValue() <= windowBottom) {
          rightWrapperRef.current.classList.add('type-absolute');
        } else {
          rightWrapperRef.current.classList.remove('type-absolute');
        }
      }
    };
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileScreen(window.innerWidth <= SCREEN_SIZES.MOBILE_SCREEN_WIDTH);
      calculateElementValue();
    };

    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  return (
    <>
      <div className={'ml-page'}>
        {isLocalLoading ? null : (
          <div className='page-inner'>
            <div className='layout-inner'>
              <div className='detail-page-wrapper'>
                <div className='detail-layout-wrapper'>
                  <div className='detail-layout-left-wrapper' ref={leftWrapperRef}>
                    {/* 데스크탑, 태블릿용 메인 이미지 */}
                    <div className={'mb-hide'}>
                      <ProductMainImgWrapper thumbnail={imageUrl} />
                    </div>
                    {/* END 데스크탑, 태블릿용 메인 이미지 */}

                    {/* 제공 영상 캐로셀: 비디오 상품 상세에만 존재 */}
                    {productDetailData?.product_type === PRODUCT_TYPES.VIDEO && clips.length > 0 ? (
                      <DetailCarousel clips={clips} />
                    ) : null}
                    {/* END 제공 영상 캐로셀: 비디오 상품 상세에만 존재 */}

                    <ProductMainDescription productDescription={product_description} />
                  </div>
                  <div className={`detail-layout-right-wrapper ${layoutTop ? 'layout-top' : ''}`} ref={rightWrapperRef}>
                    <div className='layout-inner'>
                      <div className='detail-layout-right-inner-wrapper'>
                        {/* 모바일용 메인 이미지 */}
                        <div className={'mb-show'}>
                          <ProductMainImgWrapper thumbnail={imageUrl} />
                        </div>
                        {/* END 모바일용 메인 이미지 */}

                        <ProductSubDescription productDetailData={productDetailData} />
                        <ProductVideoInputWrapper productDetailData={productDetailData} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <ScrollFixedTopButton />
    </>
  );
};

export default ProductVideoDetail;
